import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Grid2, List, ListItem, ListItemIcon, ListItemText, Typography, useTheme, Skeleton } from "@mui/material";
import { Square } from "@mui/icons-material";
import styles from "styles/ProjectList.module.css";
import ClaimsPopup from "./ClaimsPopup";
import { useParams } from "react-router-dom";
import { useGetClaimsValueQuery } from "store/slices/apis/claimsAndChangeOrderDashboardSlice";

const TotalClaimValue = () => {
    const chartRef = useRef(null);
    const theme = useTheme();
    const { projectId } = useParams();
    const [popupData, setPopupData] = useState(null);
    const [open, setOpen] = useState(false);
    const { data, isLoading } = useGetClaimsValueQuery({ projectId });
    const COLORS = {
        approved: "#3F766E",
        rejected: "#FDCA40",
        underReview: "#EC5540"
    };

    const chartData = data?.data
        ? [
            { name: "Approved", value: data?.data?.approvedValue, color: COLORS.approved },
            { name: "Rejected", value: data?.data?.rejectedValue, color: COLORS.rejected },
            { name: "Under Review", value: data?.data?.underReviewValue, color: COLORS.underReview }
        ]
        : [];

    const hasData = chartData.some(item => item.value > 0);

    const options = {
        chart: {
            type: "pie",
            height: "100%",
            backgroundColor: 'transparent',
            spacingTop: 10,
            spacingRight: 0,
            spacingBottom: 0,
            spacingLeft: 0,
        },
        title: {
            text: "",
        },
        subtitle: {
            text: "",
        },
        tooltip: {
            backgroundColor: '#00000080',
            pointFormat: "{point.y}",
            style: {
                color: '#fff',
                fontSize: '14px'
            }
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            pie: {
                allowPointSelect: false,
                cursor: "pointer",
                size: '100%',
                innerSize: "75%",
                startAngle: 0,
                borderRadius: 0,
                borderWidth: 0,
                backgroundColor: '#000',
                dataLabels: [{
                    enabled: true,
                    distance: -18,
                    format: "{point.percentage:.1f}%",
                    style: {
                        fontSize: '11px',
                        fontWeight: '600',
                        lineHeight: '100%',
                        letterSpacing: "0.17px",
                        color: '#ffffff',
                        textOutline: 'none',
                        textShadow: 'none'
                    },
                    allowOverlap: true,
                    crop: false,
                    overflow: "justify"
                }],
                showInLegend: false,
                point: {
                    events: {
                        click: function () {
                            setPopupData({
                                name: this.name,
                                value: this.value,
                            });
                            setOpen(true);
                        }
                    }
                }
            },
        },
        series: [
            {
                name: "Claim Values",
                innerSize: "60%",
                data: chartData
                    .filter(item => item.value > 0)
                    .map(item => ({
                        name: item.name,
                        y: item.value,
                        color: item.color
                    }))
            },
        ],
    };

    return (
        <Box sx={{ backgroundColor: "white", border: "1px solid #D8D8D8", borderRadius: "12px" }}>
            <Grid2 container>
                <Grid2 size={{ xs: 12, md: 7 }}>
                    <Box sx={{ margin: '0 auto', paddingLeft: "17px" }}>
                        <Typography variant="h6" sx={{ mb: 1, color: `${theme?.customSettings?.themeColor}`, fontWeight: "500", fontSize: '18px', paddingTop: "20px", lineHeight: "100%", letterSpacing: "0.4px" }}>
                            Total Claim Value
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: "#182A4E80" }}>
                            Track claim status to manage financial exposure and resolution timelines
                        </Typography>
                    </Box>
                    {isLoading ? (
                        [...Array(3)].map((_, idx) => (
                            <Grid2 container direction="row" spacing={1} key={idx} style={{ marginLeft: "30px", paddingTop: "10px" }}>
                                <Box sx={{ width: "17px", height: "17px", borderRadius: "0", backgroundColor: "#eee", marginTop: "5px" }} />
                                <Skeleton variant="text" width={100} height={24} sx={{ marginLeft: 1 }} />
                                <Skeleton variant="text" width={30} height={28} sx={{ marginLeft: 2 }} />
                            </Grid2>
                        ))
                    ) : (
                        hasData ? (
                            chartData.map((item, idx) => (
                                <Grid2 container direction="row" spacing={1} key={idx} style={{ marginLeft: "10px", paddingTop: idx === 0 ? "10px" : "0px" }}>
                                    <Typography style={{ padding: "8px", width: "25px" }}>
                                        <Square style={{ color: item?.color, height: '17px', width: '17px' }} />
                                    </Typography>
                                    <Typography style={{
                                        padding: "9px", width: "100px", color: "#182A4E", fontSize: "16px", fontWeight: "500",
                                        lineHeight: "100%", whiteSpace: "nowrap", letterSpacing: "-0.4px"
                                    }}>
                                        {item.name}: {item.value}
                                    </Typography>
                                </Grid2>
                            ))
                        ) : (
                            <Typography sx={{ color: '#999', fontSize: 16, marginLeft: 4, marginTop: 6, whiteSpace: "nowrap" }}>
                                No data available to display the chart.
                            </Typography>
                        )
                    )}
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5 }}>
                    <Typography style={{ marginTop: "60px" }}>
                        {isLoading ? (
                            <Skeleton variant="circular" width={150} height={150} sx={{ marginTop: "75px", marginBottom: "30px" }} />
                        ) : (
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={options}
                                ref={chartRef}
                            />
                        )}
                    </Typography>
                </Grid2>
            </Grid2>
            {open &&
                <ClaimsPopup title="Total Claim Value" value={popupData.name} status="Totalclaimvalue" onOpen={open} onClose={() => setOpen(false)} />
            }
        </Box>
    );
};

export default TotalClaimValue;
